import {
  type AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';

import { useAuthStore } from './authStore';

/**
 * Entra ID login for a customer deployment (lanes-product.md decision 6),
 * redirect flow. Configured by the build; without the three variables the
 * dashboard cannot sign anyone in.
 */
export type EntraConfig = { tenantId: string; clientId: string; apiScope: string };

export function getEntraConfig(): EntraConfig | null {
  const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID?.trim() ?? '';
  const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID?.trim() ?? '';
  const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE?.trim() ?? '';
  if (!tenantId || !clientId || !apiScope) return null;
  return { tenantId, clientId, apiScope };
}

export function isEntraConfigured(): boolean {
  return getEntraConfig() !== null;
}

let instance: PublicClientApplication | null = null;

function app(): PublicClientApplication {
  if (instance) return instance;
  const config = getEntraConfig();
  if (!config) throw new Error('Entra is not configured');
  instance = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: `${window.location.origin}/login`,
    },
    cache: { cacheLocation: 'localStorage' },
  });
  return instance;
}

function toSession(account: AccountInfo) {
  return { username: account.username, name: account.name ?? account.username, oid: account.localAccountId };
}

/** Before the first render: finish a redirect in flight, restore the account. */
export async function initializeAuth(): Promise<void> {
  if (!isEntraConfigured()) return;
  const pca = app();
  await pca.initialize();
  const result = await pca.handleRedirectPromise();
  const account = result?.account ?? pca.getAllAccounts()[0] ?? null;
  if (account) {
    pca.setActiveAccount(account);
    useAuthStore.getState().setSession(toSession(account));
  }
}

export async function loginWithEntra(): Promise<void> {
  const config = getEntraConfig();
  if (!config) throw new Error('Entra is not configured');
  await app().loginRedirect({ scopes: [config.apiScope] });
}

export async function logoutEntra(): Promise<void> {
  useAuthStore.getState().setSession(null);
  if (!isEntraConfigured()) return;
  const pca = app();
  await pca.logoutRedirect({ account: pca.getActiveAccount() ?? undefined });
}

let inFlight: Promise<string | null> | null = null;

export async function getEntraAccessToken(): Promise<string | null> {
  const config = getEntraConfig();
  if (!config) return null;
  const pca = app();
  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  if (!account) return null;
  if (!inFlight) {
    inFlight = pca
      .acquireTokenSilent({ scopes: [config.apiScope], account })
      .then((r) => r.accessToken)
      .catch(async (err: unknown) => {
        if (err instanceof InteractionRequiredAuthError) {
          await pca.acquireTokenRedirect({ scopes: [config.apiScope], account });
          return null;
        }
        console.warn('token acquisition failed; signing out', err);
        useAuthStore.getState().setSession(null);
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}
